import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import tenant management components (lazy-loaded for performance)
const TenantsList = React.lazy(() => import("./TenantsList"));
const CreateTenant = React.lazy(() => import("./CreateTenant"));
const EditTenant = React.lazy(() => import("./EditTenant"));
const QuickDeploy = React.lazy(() => import("./QuickDeploy"));
const StorageManagement = React.lazy(() => import("./StorageManagement"));
const StorageUsage = React.lazy(() => import("./StorageUsage"));
const AIUsage = React.lazy(() => import("./AIUsage"));
const SMSUsage = React.lazy(() => import("./SMSUsage"));
const MasterPayAudit = React.lazy(() => import("./MasterPayAudit"));
const KnowledgeFolders = React.lazy(() => import("./KnowledgeFolders"));
// SubscriptionBilling removed — consolidated into bills module

const tenantManagementModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "tenant-management",
    name: "Tenant Management",
    description: "Manage tenant accounts, configurations, and module deployments",
    version: "1.0.0",
    enabled: true,
    category: "administration",
    order: 95, // Before admin module
    icon: "🏢",
    color: "#4299E1",

    // 🛣️ ROUTES
    //
    // URLs are `/tenant-management/*` so the module id, the folder name and the URL
    // namespace all read the same (how-to-build-new-module § Key File Locations).
    // They were `/admin/tenants/*` until 2026-08-07; App.tsx keeps every old path
    // alive as a redirect, so existing bookmarks and #hash deep links still resolve.
    //
    // `permissionLevel` is the SSOT pattern: App.tsx swaps in the backend-resolved
    // array for that level. The `permissions` array left on each route is NOT drift —
    // it is the fail-CLOSED fallback for the window before the modulePermissions query
    // resolves. App.tsx returns the module untouched while `backendPerms[id]` is
    // undefined, and ProtectedRoute treats an empty array as "any authenticated user",
    // so a route with no fallback is briefly open to every logged-in user. On this
    // module — the most privileged surface in the app — that window is not acceptable.
    routes: [
        {
            path: "/tenant-management",
            component: TenantsList,
            permissionLevel: "view",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
            layoutOptions: { py: 4 },
        },
        {
            path: "/tenant-management/new",
            component: CreateTenant,
            permissionLevel: "create",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            path: "/tenant-management/:id/edit",
            component: EditTenant,
            permissionLevel: "edit",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
            layoutOptions: { py: 4 },
        },
        {
            path: "/tenant-management/quick-deploy",
            component: QuickDeploy,
            permissionLevel: "edit",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            // Deletes/unpins IPFS files, so it gates on `edit`, not `view`
            path: "/tenant-management/storage",
            component: StorageManagement,
            permissionLevel: "edit",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            path: "/tenant-management/ai-usage",
            component: AIUsage,
            permissionLevel: "view",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            path: "/tenant-management/sms-usage",
            component: SMSUsage,
            permissionLevel: "view",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            path: "/tenant-management/storage-usage",
            component: StorageUsage,
            permissionLevel: "view",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        {
            // Cross-tenant AI Pay Audit — Tom-only cockpit for adjudicating any tenant's pay run.
            // `aiAudit` (not `view`) — see the matching note in the backend manifest.
            path: "/tenant-management/pay-audit",
            component: MasterPayAudit,
            permissionLevel: "aiAudit",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN", "PAY_AI_AUDITOR"],
            useLayout: true,
        },
        {
            // Knowledge Folders manager — list/create biz-in-a-folders + tenant→folder map
            path: "/tenant-management/knowledge-folders",
            component: KnowledgeFolders,
            permissionLevel: "edit",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"],
            useLayout: true,
        },
        // Subscription Billing route removed — now at /bills/subscription-billing
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Tenants",
            path: "/tenant-management",
            icon: "🏢",
            permissionLevel: "view",
        },
        {
            label: "Pay Audit (cross-tenant)",
            path: "/tenant-management/pay-audit",
            icon: "🤖",
            permissionLevel: "aiAudit",
        },
        {
            label: "Create Tenant",
            path: "/tenant-management/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Quick Deploy",
            path: "/tenant-management/quick-deploy",
            icon: "⚡",
            permissionLevel: "edit",
        },
        {
            label: "Storage Management",
            path: "/tenant-management/storage",
            icon: "💾",
            permissionLevel: "edit",
        },
        {
            label: "AI Usage",
            path: "/tenant-management/ai-usage",
            icon: "🧠",
            permissionLevel: "view",
        },
        {
            label: "SMS Usage",
            path: "/tenant-management/sms-usage",
            icon: "📱",
            permissionLevel: "view",
        },
        {
            label: "Storage Usage",
            path: "/tenant-management/storage-usage",
            icon: "📊",
            permissionLevel: "view",
        },
        {
            label: "Knowledge Folders",
            path: "/tenant-management/knowledge-folders",
            icon: "📚",
            permissionLevel: "edit",
        },
        // Subscription Billing nav removed — now in bills module
    ],

    // 🔐 PERMISSIONS — EMPTY BY DESIGN. The backend manifest
    // (business-builder-backend/src/modules/tenants/permissions.ts, moduleId
    // 'tenant-management') is the single source of truth; App.tsx merges the resolved
    // arrays in at runtime. Nav visibility fails CLOSED off these — FloatingNavbar
    // filters on `permissions.view`, so the module stays hidden until the backend
    // answers. Do not re-hardcode: from the module's creation until 2026-08-07 these
    // arrays said ADMIN + TENANT_MASTER_ADMIN while the backend also granted
    // TENANT_ADMIN, so a TENANT_ADMIN passed every resolver gate and still could not
    // see the module in the navbar.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: []
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Create Tenant",
            path: "/tenant-management/new",
            icon: "🏢",
            description: "Add a new tenant to the system"
        },
        {
            label: "Quick Deploy",
            path: "/tenant-management/quick-deploy",
            icon: "⚡",
            description: "Deploy modules to a single tenant"
        },
        {
            label: "Storage Management",
            path: "/tenant-management/storage",
            icon: "💾",
            description: "View and manage IPFS storage across tenants"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.2.0",
            releaseDate: "2026-08-07",
            title: "Module identity + URL alignment",
            features: [
                "🔨 URLs moved from /admin/tenants/* to /tenant-management/* — module id, folder and URL now match",
                "🔨 Old /admin/tenants/* paths kept as redirects, so bookmarks and #hash deep links still work",
                "🐛 Backend moduleId renamed 'tenants' → 'tenant-management' — permission resolution had NEVER matched the frontend id",
                "🐛 TENANT_ADMIN can now see the module in the navbar (backend granted view; the hardcoded frontend array did not)",
                "✨ Permissions migrated to the backend single-source-of-truth pattern",
                "✨ Module registered in MODULE_REGISTRY — now appears in /admin/module-upgrades",
                "🔨 Cross-tenant Pay Audit moved to /tenant-management/pay-audit on its own aiAudit permission level"
            ],
            notes: "Identity/routing alignment pass. No functional change to tenant administration itself."
        },
        {
            version: "1.1.0",
            releaseDate: "2026-01-31",
            title: "IPFS Storage Management",
            features: [
                "✨ New Storage Management page for viewing all IPFS files",
                "✨ Storage breakdown by tenant and source (Media CDN vs Drone)",
                "✨ File preview for images and videos",
                "✨ Delete files (unpin from IPFS + database)",
                "✨ Bulk delete functionality",
                "✨ Filter by tenant, source, and file type"
            ],
            notes: "Master admin can now view and manage IPFS storage across all tenants."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready multi-tenant management",
                "✅ Quick Deploy with local storage persistence",
                "✅ Mass Deploy for multiple tenants",
                "✅ Comprehensive tenant configuration",
                "✅ Module deployment automation",
                "✅ Payment receiving details management",
                "✅ Streamlined tenant creation workflow"
            ],
            notes: "Official stable release with advanced deployment automation and tenant management."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-11-21",
            title: "Tenant Configuration Improvements",
            features: [
                "✨ Enhanced tenant page with improved UI (408 lines)",
                "✨ Better tenant record management (314 insertions)",
                "✨ Simplified tenant module configuration (34 deletions)",
                "✨ Improved form handling and validation"
            ],
            notes: "Major tenant configuration page improvements for better usability."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-11-04",
            title: "GitHub Repository Tracking",
            features: [
                "✨ Added GitHub repository badges (18 lines)",
                "✨ Visual indicators for repos without GitHub integration",
                "✨ Better deployment status tracking"
            ],
            notes: "GitHub integration status visibility for deployments."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-11-02",
            title: "Mass Deployment System",
            features: [
                "✅ Created MassDeploy component (855 lines)",
                "✨ Enhanced QuickDeploy with browser storage (684 lines refactored)",
                "✨ Updated TenantsList for mass operations (40 lines)",
                "✨ Local storage for deployment settings",
                "✨ Faster mass module deployment across multiple tenants",
                "✨ Support for updating multiple modules simultaneously"
            ],
            notes: "Revolutionary mass deployment system for multi-tenant module updates."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-10-27",
            title: "Payment Receiving Details",
            features: [
                "✨ Added payment receiving details to tenant model (654 lines)",
                "✨ Enhanced EditTenant with payment configuration (651 insertions)",
                "✨ Better financial management capabilities"
            ],
            notes: "Payment receiving and financial configuration features."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-10-08",
            title: "Simplified Tenant Form",
            features: [
                "🔨 Major simplification of CreateTenant (1,429 deletions, 178 insertions)",
                "✨ Streamlined tenant creation workflow",
                "✨ Reduced complexity and improved UX",
                "✨ Faster tenant onboarding process"
            ],
            notes: "Major form simplification for better tenant creation experience."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-09-30",
            title: "Initial Tenant Management System",
            features: [
                "✅ Created CreateTenant component (1,388 lines)",
                "✅ Created DeploymentManager component (455 lines)",
                "✅ Created EditTenant component (2,756 lines)",
                "✅ Created TenantDeploymentManager (1,178 lines)",
                "✅ Created TenantsList component (1,231 lines)",
                "✅ Separated from admin module for better organization",
                "✅ Module deployment functionality",
                "✅ Comprehensive tenant configuration",
                "✅ Quick Deploy implementation (488 lines)",
                "✅ ModuleDeployment refactoring"
            ],
            notes: "Initial implementation separated from admin module with 7,113 lines of tenant-specific code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default tenantManagementModuleConfig;